import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { useAuth } from '../../context/AuthContext';
import { useTranslation, Trans } from 'react-i18next';
import { regionList } from '../../regions/regionConfig';
import { useRegion } from '../../context/RegionContext';
import './Header.css';

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authStatus, isSuperuser, hasModelAccess, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const { region, changeRegion, allowedRegions } = useRegion();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPlatformOpen, setIsPlatformOpen] = useState(false);

  const visibleRegions = allowedRegions === null
    ? regionList
    : regionList.filter(r => allowedRegions.includes(r.id));

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate({ to: '/' });
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const scrollToContact = () => {
    if (location.pathname !== '/') {
      navigate({ to: '/' });
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderAuthLinks = () => {
    if (authStatus === 'loading') return null;

    if (authStatus === 'authenticated') {
      return (
        <>
          <li className="nav-item dropdown-platform">
            <button
              className="nav-button dropdown-toggle"
              onClick={() => setIsPlatformOpen(!isPlatformOpen)}
              onBlur={() => setTimeout(() => setIsPlatformOpen(false), 200)}
            >
              {t('header.platform')} ▾
            </button>
            {isPlatformOpen && (
              <ul className="dropdown-menu-platform">
                <li><Link to="/select-service" className={location.pathname === '/select-service' ? 'active' : ''}>📋 {t('header.select_service')}{location.pathname === '/select-service' && ' ✔'}</Link></li>
                <li><Link to="/maininteractivemap" className={location.pathname === '/maininteractivemap' ? 'active' : ''}>🗺 {t('header.visualizer')}{location.pathname === '/maininteractivemap' && ' ✔'}</Link></li>
                <li><Link to="/forecast" className={location.pathname === '/forecast' ? 'active' : ''}>📈 {t('header.forecast')}{location.pathname === '/forecast' && ' ✔'}</Link></li>
                <li><Link to="/eddies" className={location.pathname === '/eddies' ? 'active' : ''}>🌀 {t('header.eddies')}{location.pathname === '/eddies' && ' ✔'}</Link></li>
                <li><Link to="/vientos-olas" className={location.pathname === '/vientos-olas' ? 'active' : ''}>🌬 {t('header.wind_waves')}{location.pathname === '/vientos-olas' && ' ✔'}</Link></li>
                <li><Link to="/dashboard_oleaje" className={location.pathname === '/dashboard_oleaje' ? 'active' : ''}>🌊 {t('header.wave_dashboard')}{location.pathname === '/dashboard_oleaje' && ' ✔'}</Link></li>
                <li><Link to="/mareas" className={location.pathname === '/mareas' ? 'active' : ''}>🌙 {t('header.tides')}{location.pathname === '/mareas' && ' ✔'}</Link></li>
                <li><Link to="/mascara" className={location.pathname === '/mascara' ? 'active' : ''}>🎭 {t('header.mask')}{location.pathname === '/mascara' && ' ✔'}</Link></li>
                <li><Link to="/modelos_predictivos/especie" className={location.pathname.startsWith('/modelos_predictivos') ? 'active' : ''}>🧬 {t('header.predictive_models')}{location.pathname.startsWith('/modelos_predictivos') && ' ✔'}</Link></li>
                {hasModelAccess && (
                  <li><Link to="/zonas_pesca_ia" className={location.pathname.startsWith('/zonas_pesca_ia') ? 'active' : ''}>🎯 {t('header.zones_ia')}{location.pathname.startsWith('/zonas_pesca_ia') && ' ✔'}</Link></li>
                )}
              </ul>
            )}
          </li>
          {isSuperuser && (
            <li className="nav-item">
              <Link to="/admin">{t('header.admin')}</Link>
            </li>
          )}
          <li className="nav-item">
            <button onClick={handleLogout} className="nav-button">{t('header.logout')}</button>
          </li>
        </>
      );
    }

    if (authStatus === 'unauthenticated') {
      return (
        <li className="nav-item">
          <Link to="/login">{t('header.login')}</Link>
        </li>
      );
    }

    return null;
  };

  return (
    <nav className="navbar-custom">
      <div className="navbar-container">
        <Link to="/" className="navbar-brand-custom">
          <img src="/logo_bluenetfish.png" alt="Bluenetfish Logo" />
          <span>Bluenetfish</span>
          <img src="/logo_albor.jpg" alt="Albor Tecnologico" className="logo-albor" />
        </Link>

        <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          &#9776;
        </button>

        <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          <li className="nav-item">
            <Link to="/">{t('header.home')}</Link>
          </li>
          <li className="nav-item">
            <Link to="/guia_usuario">{t('header.guide')}</Link>
          </li>
          <li className="nav-item">
            <button onClick={scrollToContact} className="nav-button">{t('header.contact')}</button>
          </li>
          {renderAuthLinks()}
          {authStatus === 'authenticated' && (
            <li className="nav-item region-selector">
              {visibleRegions.map(r => (
                <button
                  key={r.id}
                  className={`region-btn ${region === r.id ? 'active' : ''}`}
                  onClick={() => changeRegion(r.id)}
                  title={r.name}
                >{i18n.language === 'ar' ? (r.nameAr || r.name) : i18n.language === 'en' ? (r.nameEn || r.name) : i18n.language === 'ko' ? (r.nameKo || r.name) : r.name}</button>
              ))}
            </li>
          )}
          <li className="nav-item language-selector">
            <button
              className={`lang-btn ${i18n.language === 'es' ? 'active' : ''}`}
              onClick={() => changeLanguage('es')}
            >Español</button>
            <button
              className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
              onClick={() => changeLanguage('en')}
            >English</button>
            <button
              className={`lang-btn ${i18n.language === 'ar' ? 'active' : ''}`}
              onClick={() => changeLanguage('ar')}
            >العربية</button>
            <button
              className={`lang-btn ${i18n.language === 'ko' ? 'active' : ''}`}
              onClick={() => changeLanguage('ko')}
            >한국어</button>
          </li>
        </ul>
      </div>
    </nav>
  );
}

export default Header;
